import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { competitionRoundsService } from '../../services/competition-rounds';
import { formatLeadTime } from '../../components/competition/lead-time';
import type { UnclassedBlock, UnclassedResult } from '../../types/competition-round';

interface UnclassedBlocksCardProps {
  slug: string;
  roundId: string;
  isLead: boolean;
  /**
   * Changes whenever the round's shape does — a class ticked or unticked, a
   * block added or removed — which is when the list is read again: unticking a
   * class can leave one of its judged blocks here.
   */
  refreshKey: string;
  /** A problem was removed: the page reads the round again for its counts. */
  onRemoved: () => void;
}

/** 10.5 is "10+": the hold reached, and a controlled move out of it. */
function formatHolds(holds: number): string {
  return Number.isInteger(holds) ? String(holds) : `${Math.floor(holds)}+`;
}

/**
 * The round's problems that belong to no class, on its setup.
 *
 * A problem of no class is climbed by **every** class of the round, so one that
 * nobody meant to be there quietly lengthens everybody's card — and makes the
 * turns of a class impossible to rank together. They are usually what a
 * category left behind when it was deleted or taken off the round: the empty
 * ones go with it now, the judged ones stay, as evidence, and land here.
 *
 * Each is shown with the cards already written on it, so removing one is a
 * decision taken with what it loses in view; one with cards asks first, and the
 * cards go with it.
 */
export default function UnclassedBlocksCard({ slug, roundId, isLead, refreshKey, onRemoved }: UnclassedBlocksCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [blocks, setBlocks] = useState<UnclassedBlock[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Neither `t` nor `toast` is a dependency: a re-render must never re-fetch. A
  // failure leaves the list empty — this card is a cleanup aid on a page about
  // something else, not worth an error of its own.
  useEffect(() => {
    let cancelled = false;
    competitionRoundsService
      .getUnclassedBlocks(slug, roundId)
      .then((list) => {
        if (!cancelled) setBlocks(list);
      })
      .catch(() => {
        if (!cancelled) setBlocks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, roundId, refreshKey]);

  const summary = (result: UnclassedResult) => {
    if (isLead) {
      if (result.holds_count === null) return t('gymAdmin.rounds.unclassed.noCard');
      const parts = [t('gymAdmin.rounds.unclassed.holds', { holds: formatHolds(result.holds_count) })];
      if (result.time_seconds !== null) parts.push(formatLeadTime(result.time_seconds));
      if (result.climbed_as === 'top_rope') parts.push(t('gymAdmin.rounds.unclassed.topRope'));
      return parts.join(' · ');
    }

    const outcome = result.topped
      ? t('gymAdmin.rounds.unclassed.top')
      : result.zone
        ? t('gymAdmin.rounds.unclassed.zone')
        : t('gymAdmin.rounds.unclassed.noTop');
    return `${outcome} · ${t('gymAdmin.rounds.unclassed.attempts', { count: result.attempts_count ?? 0 })}`;
  };

  const remove = async (block: UnclassedBlock) => {
    const judged = block.results.length > 0;
    if (
      judged &&
      !window.confirm(
        t('gymAdmin.rounds.unclassed.confirmDiscard', { name: block.display_name, count: block.results.length })
      )
    ) {
      return;
    }

    setBusyId(block.id);
    try {
      await competitionRoundsService.removeClimbFromRound(slug, roundId, block.id, { discardResults: judged });
      setBlocks((current) => current.filter((candidate) => candidate.id !== block.id));
      onRemoved();
    } catch (error) {
      toast({
        title: t('gymAdmin.rounds.unclassed.removeError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  if (blocks.length === 0) return null;

  return (
    <Card className="border-amber-500/50" data-testid="unclassed-blocks">
      <CardHeader>
        <CardTitle className="text-base">
          {t('gymAdmin.rounds.unclassed.title', { count: blocks.length })}
        </CardTitle>
        <CardDescription>{t('gymAdmin.rounds.unclassed.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {blocks.map((block) => (
          <div key={block.id} className="space-y-2 rounded-lg border p-3" data-testid="unclassed-block">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium">{block.display_name}</p>
                {block.climb_name && block.climb_name !== block.display_name && (
                  <p className="truncate text-xs text-muted-foreground">{block.climb_name}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {block.results.length === 0
                    ? t('gymAdmin.rounds.unclassed.noResults')
                    : t('gymAdmin.rounds.unclassed.resultsCount', { count: block.results.length })}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 gap-2"
                onClick={() => remove(block)}
                disabled={busyId === block.id}
                aria-label={t('gymAdmin.rounds.unclassed.removeLabel', { name: block.display_name })}
              >
                {busyId === block.id ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
                {t('gymAdmin.rounds.unclassed.remove')}
              </Button>
            </div>

            {block.results.length > 0 && (
              <ul className="space-y-1 border-t pt-2 text-sm" data-testid="unclassed-results">
                {block.results.map((result) => (
                  <li key={result.id} className="flex flex-wrap justify-between gap-x-3">
                    <span className="min-w-0 truncate">
                      {result.athlete ?? t('gymAdmin.rounds.unclassed.unnamed')}
                    </span>
                    <span className="text-muted-foreground">{summary(result)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
